import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ComputedAppointmentStatusLabel,
  computedAppointmentStatusAccessorFn,
} from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Icon,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Switch,
  Text,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { formatHumanName, getUserName } from '@eluve/utils';

import { RegenerateNoteDialog } from './RegenerateNoteDialog';
import {
  isActiveAppointmentStatus,
  isAppointmentWithNoteStatus,
  isRecoverableStatus,
} from './noteRecoveryEligibility';

/**
 * Note recovery is only offered for recent appointments — older notes are out of
 * scope (the supporting context, audio, and pipeline state age out). Kept in
 * sync with the copy in RegenerateNotesPage.
 */
export const RECOVERY_WINDOW_DAYS = 7;

const ALL = 'all';

const tenantAppointmentsForRecoveryQuery = graphql(`
  query tenantAppointmentsForRecovery(
    $tenantId: uuid!
    $since: timestamptz!
    $limit: Int!
  ) {
    appointments(
      where: { tenantId: { _eq: $tenantId }, startDate: { _gte: $since } }
      orderBy: { startDate: DESC_NULLS_LAST }
      limit: $limit
    ) {
      __typename
      tenantId
      id
      name
      startDate
      statusLabel
      transcription {
        __typename
        id
        transcript
      }
      user {
        __typename
        id
        firstName
        lastName
        email
      }
      patient {
        __typename
        id
        firstName
        lastName
      }
    }
  }
`);

const APPOINTMENTS_LIMIT = 200;

type Row = {
  tenantId: string;
  id: string;
  name: string;
  patientName: string;
  startDate: string | null;
  statusLabel: string;
  doctorId: string;
  doctorName: string;
  isActive: boolean;
};

interface RegenerateActionCellProps {
  row: Row;
}

const RegenerateActionCell: React.FC<RegenerateActionCellProps> = ({ row }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setIsDialogOpen(true)}>
        <Icon name="RotateCw" size="auto" />
        Regenerate note
      </Button>
      {isDialogOpen && (
        <RegenerateNoteDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          tenantId={row.tenantId}
          appointmentId={row.id}
          appointmentName={row.name}
          isActiveAppointment={row.isActive}
        />
      )}
    </>
  );
};

const columns = new ColDefBuilder<Row>()
  .defaultSortable('patientName', {
    label: 'Patient',
    cellRenderer: ({ tenantId, id, name, patientName }) => (
      <Stack gap={0}>
        <Text className="privacy-text">{patientName}</Text>
        <Link
          to={`/organizations/${tenantId}/appointments/${id}?tab=summary`}
          className="text-sm text-text-accent-secondary hover:underline"
        >
          {name}
        </Link>
      </Stack>
    ),
  })
  .dateSortable('startDate', 'Start Date')
  .defaultSortable('doctorName', 'User')
  .defaultSortable('statusLabel', {
    label: 'Status',
    cellRenderer: ({ statusLabel }) => (
      <ComputedAppointmentStatusLabel statusLabel={statusLabel} />
    ),
  })
  .colDef({
    id: 'actions',
    header: 'Actions',
    enableSorting: false,
    cell: ({ row }) => <RegenerateActionCell row={row.original} />,
  })
  .build();

export interface RegenerateNotesTableProps {
  tenantId: string;
  /**
   * ISO start of the recovery window. Computed by the parent (which does not
   * suspend) so it stays stable across `useSuspenseQuery` retries — computing it
   * here would recompute `new Date()` on every suspend and refetch forever.
   */
  since: string;
}

export const RegenerateNotesTable: React.FC<RegenerateNotesTableProps> = ({
  tenantId,
  since,
}) => {
  const { data } = useSuspenseQuery(tenantAppointmentsForRecoveryQuery, {
    variables: { tenantId, since, limit: APPOINTMENTS_LIMIT },
  });

  const [doctorFilter, setDoctorFilter] = useState<string>(ALL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [showWithNotes, setShowWithNotes] = useState(false);

  const allRows = useMemo<Row[]>(() => {
    return (data?.appointments ?? [])
      .filter((appointment) => {
        const statusLabel = appointment.statusLabel ?? '';
        // Only list appointments that can actually be recovered: the encounter
        // is active or completed with no note yet, AND there is a usable
        // transcript to generate from.
        const hasUsableTranscript = Boolean(
          appointment.transcription?.transcript?.trim(),
        );
        return isRecoverableStatus(statusLabel) && hasUsableTranscript;
      })
      .map<Row>((appointment) => {
        const statusLabel = appointment.statusLabel ?? '';
        return {
          tenantId: appointment.tenantId,
          id: appointment.id,
          name: appointment.name,
          patientName: formatHumanName(
            appointment.patient?.firstName,
            appointment.patient?.lastName,
          ),
          startDate: appointment.startDate ?? null,
          statusLabel,
          doctorId: appointment.user?.id ?? '',
          doctorName: getUserName({
            firstName: appointment.user?.firstName,
            lastName: appointment.user?.lastName,
            email: appointment.user?.email,
          }),
          isActive: isActiveAppointmentStatus(statusLabel),
        };
      });
  }, [data]);

  const doctorOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const row of allRows) {
      if (row.doctorId) byId.set(row.doctorId, row.doctorName);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allRows]);

  const statusOptions = useMemo(() => {
    const values = [...new Set(allRows.map((row) => row.statusLabel))];
    return values
      .map((value) => ({
        value,
        label: computedAppointmentStatusAccessorFn(value),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  const rows = useMemo(
    () =>
      allRows.filter(
        (row) =>
          (doctorFilter === ALL || row.doctorId === doctorFilter) &&
          (statusFilter === ALL || row.statusLabel === statusFilter) &&
          // Appointments that already have a (draft) note are hidden unless the
          // operator opts in.
          (showWithNotes || !isAppointmentWithNoteStatus(row.statusLabel)),
      ),
    [allRows, doctorFilter, statusFilter, showWithNotes],
  );

  return (
    <Stack gap={3} align="stretch">
      <Stack direction="horizontal" gap={3}>
        <Stack gap={1}>
          <Text size="xs" color="supporting">
            User
          </Text>
          <Select value={doctorFilter} onValueChange={setDoctorFilter}>
            <SelectTrigger className="min-w-52" aria-label="Filter by user">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All users</SelectItem>
              {doctorOptions.map((doctor) => (
                <SelectItem key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
        <Stack gap={1}>
          <Text size="xs" color="supporting">
            Status
          </Text>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="min-w-44" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {statusOptions.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
        <Stack direction="horizontal" gap={2} align="center" className="pt-6">
          <Switch
            checked={showWithNotes}
            onCheckedChange={setShowWithNotes}
            aria-label="Show appointments with notes"
          />
          <Label className="text-sm text-nowrap">
            Show appointments with notes
          </Label>
        </Stack>
      </Stack>
      <DataTable
        enableGlobalSearch={true}
        columns={columns}
        data={rows}
        initialSortingState={[{ id: 'startDate', desc: true }]}
      />
    </Stack>
  );
};
